import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import { AuthPage } from "../components/auth-page.js";
import { apiClient } from "../lib/api.js";
import { Button, ErrorState, Label, TextField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";

/**
 * Sign in (FR-005, FR-006, FR-009).
 *
 * PUBLIC — outside `_authenticated`, because someone signing in by definition has no session.
 */
export const Route = createFileRoute("/sign-in")({
  component: SignIn,
});

function SignIn(): ReactNode {
  const t = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  /*
   * IN FLIGHT, AND WHY THIS SCREEN NEEDS IT MORE THAN MOST.
   *
   * The request sends an EMAIL. There is no optimistic render to watch and no row appearing in a
   * table — the only feedback is the panel swapping to "check your email" once the round trip
   * finishes, and on a slow connection that is a second or two of a page that looks like it ignored
   * the press. The reliable next move for a member is to press it again, which sends a second magic
   * link and invalidates the first: the one they then click is the wrong one.
   */
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState<unknown>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function send(): Promise<void> {
    /*
     * THE FAILURE PATH IS WHAT MAKES THE SPINNER SAFE TO ADD.
     *
     * This used to be three unguarded lines: a rejected request left the promise unhandled and the
     * screen unchanged, which was merely unhelpful while nothing was spinning. With a pending state
     * it becomes a control that spins for ever — strictly worse than no indicator, because it claims
     * something is still happening. `finally` is the load-bearing part; the message is the courtesy.
     */
    setSending(true);
    setFailed(null);
    try {
      await apiClient.post("/auth/sign-in", { email });
      setSent(true);
      setCooldown(60);
    } catch (error) {
      setFailed(error);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    /*
     * `key` matters. Without it React reconciles this against the form branch — same component, same
     * position — and the panel never remounts, so the reveal does not replay and the confirmation
     * snaps into place. The key makes the two states distinct elements, so each one arrives.
     */
    return (
      <AuthPage key="sent">
        <h1 className="text-2xl font-semibold tracking-tight">{t("auth.checkYourEmail")}</h1>
        {/* Says nothing about whether the address has an account (FR-006). */}
        <p className="mt-2 text-sm text-muted">
          If an account exists for {email}, we&apos;ve sent a sign-in link. Check your spam folder.
        </p>
        {/* The resend is the same request, so it gets the same treatment. `disabled` is OR-ed with
            `pending` inside `Button`, so the cooldown still holds while nothing is in flight. */}
        <Button
          type="button"
          className="mt-6"
          disabled={cooldown > 0}
          pending={sending}
          onClick={() => void send()}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : t("auth.resend")}
        </Button>
        {failed === null ? null : <ErrorState className="mt-4">{errorSentence(failed)}</ErrorState>}
      </AuthPage>
    );
  }

  return (
    <AuthPage key="form">
      <h1 className="text-2xl font-semibold tracking-tight">{t("auth.signInHeading")}</h1>
      <p className="mt-2 text-sm text-muted">{t("auth.signInHint")}</p>

      <form
        className="mt-8"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <Label htmlFor="email">{t("auth.email")}</Label>
        <TextField
          id="email"
          type="email"
          placeholder="you@example.com"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button variant="primary" className="mt-6 w-full" type="submit" pending={sending}>
          {t("auth.emailMeALink")}
        </Button>

        {/*
          A REFUSAL HAS TO REACH THE SCREEN. FR-006 keeps the SUCCESS path silent about whether an
          account exists — that is what the confirmation panel's wording protects — and this is a
          different thing: the request itself did not complete, so there is nothing to be discreet
          about and everything to say.
        */}
        {failed === null ? null : <ErrorState className="mt-4">{errorSentence(failed)}</ErrorState>}
      </form>
    </AuthPage>
  );
}
